<!--
 * @Description: 阿里播放器组件，支持清晰度切换和网络状态监控
-->
<template>
	<div :id="id" class="playerBox setting-none">
		<!-- 清晰度切换提示 -->
		<transition name="quality-tip-slide">
			<div v-if="showQualityTip" class="quality-tip-card">
				<div class="quality-tip-header">
					<span class="quality-tip-icon">⚠️</span>
					<span class="quality-tip-title">清晰度提示</span>
					<span class="quality-tip-close" @click="handleTipClose">✕</span>
				</div>
				<div class="quality-tip-text">{{ qualityTipText }}</div>
				<div v-if="availableLowerQuality.exists" class="quality-tip-actions">
					<button class="tip-btn tip-btn-cancel" @click="handleTipCancel">稍后再试</button>
					<button class="tip-btn tip-btn-confirm" @click="handleTipConfirm">立即切换</button>
				</div>
			</div>
		</transition>
		<!-- Toast 提示 -->
		<div v-if="showToast" class="toast-toast">
			{{ toastMessage }}
		</div>
	</div>
</template>

<script>
import {aliPlayerJs} from '@/utils/util'

const QUALITY_ORDER = ['1080p', '960p', '720p', '480p']
const FILENAME_TO_QUALITY = {
	'-hd.mp4': '1080p', '-hd.m3u8': '1080p',
	'-sd.mp4': '960p', '-sd.m3u8': '960p',
	'-fd.mp4': '720p', '-fd.m3u8': '720p',
	'-ld.mp4': '480p', '-ld.m3u8': '480p'
}
const DEFINITION_TO_QUALITY = { 'OD': '1080p', 'HD': '1080p', 'SD': '960p', 'FD': '720p', 'LD': '480p' }
const DESC_TO_QUALITY = { '超清': '1080p', '高清': '960p', '流畅': '720p', '标清': '480p' }
const QUALITY_NAMES = { '1080p': '超清', '960p': '高清', '720p': '流畅', '480p': '标清' }

export default {
	name: 'aliPlayer',
	props: {
		aliPlayOptions: { type: Object, default: () => ({}) },
		meetingId: { type: String, default: '' },
		id: { type: String, default: 'player-con' }
	},
	data() {
		return {
			player: null,
			speed: true,
			// 清晰度相关
			showQualityTip: false,
			qualityTipText: '当前网络环境不佳，建议切换到较低清晰度以获得更好的播放体验',
			currentQualityIndex: 0,
			videoUrls: {},
			fallbackVideoUrls: {},
			currentMediaType: 'mp4',
			qualityDefinitions: [],
			srcCheckTimer: null,
			// Toast 相关
			showToast: false,
			toastMessage: '',
			toastTimer: null,
			// 重试相关
			retryCount: 0,
			maxRetries: 3,
			// 播放状态检测相关
			isVideoStalled: false,
			stallStartTime: 0,
			lastCurrentTime: 0,
			lastUpdateTime: 0,
			playbackCheckTimer: null,
			// 优化：缓冲状态检测
			isBuffering: false,
			bufferingStartTime: 0,
			// 优化：卡顿计数器（容错机制）
			stallCount: 0,
			// 优化：弹窗控制
			lastTipShowTime: 0, // 上次弹窗显示时间
			minTipInterval: 10000, // 最小弹窗间隔10秒
			autoCloseDelay: 8000, // 自动关闭延时8秒
			autoCloseTimer: null, // 自动关闭定时器
			initOptions: {
				id: 'player-con',
				source: '',
				width: '100%',
				height: document.body.clientWidth / 16 * 9 + 'px',
				autoplay: false,
				isLive: false,
				rePlay: false,
				playsinline: true,
				preload: true,
				controlBarVisibility: 'click',
				showBarTime: 5000,
				useH5Prism: true,
				x5_type: 'H5',
				x5_fullscreen: false,
				x5_video_position: 'normal',
				x5_orientation: 'landscape',
				qualitySort: 'desc',
				defaultDefinition: 'HD',
				skinLayout: [
					{ name: 'bigPlayButton', align: 'blabs', x: 30, y: 80 },
					{ name: 'H5Loading', align: 'cc' },
					{ name: 'errorDisplay', align: 'tlabs', x: 0, y: 0 },
					{ name: 'infoDisplay' },
					{ name: 'tooltip', align: 'blabs', x: 0, y: 56 },
					{ name: 'thumbnail' },
					{
						name: 'controlBar',
						align: 'blabs', x: 0, y: 0,
						children: [
							{ name: 'progress', align: 'blabs', x: 0, y: 44 },
							{ name: 'playButton', align: 'tl', x: 15, y: 12 },
							{ name: 'timeDisplay', align: 'tl', x: 10, y: 7 },
							{ name: 'fullScreenButton', align: 'tr', x: 10, y: 12 },
							{ name: 'volume', align: 'tr', x: 5, y: 10 }
						]
					}
				],
				components: [{ name: 'QualityComponent', type: AliPlayerComponent.QualityComponent }]
			}
		}
	},
	computed: {
		availableLowerQuality() {
			const availableQualities = QUALITY_ORDER.filter(q => this.videoUrls[q])
			const nextIndex = this.currentQualityIndex + 1
			const quality = nextIndex < availableQualities.length ? availableQualities[nextIndex] : null
			return {
				exists: !!quality,
				quality: quality
			}
		}
	},
	watch: {
		aliPlayOptions: {
			deep: true,
			immediate: true,
			handler(options) {
				if(!options?.source?.length) return
				aliPlayerJs().then((res) => res && this.measureBW(this.onPlayer))
			}
		}
	},
	destroyed() {
		const dom = document.querySelector(`#${this.id} video`)
		dom?.removeEventListener('webkitendfullscreen', this.addEvent)

		clearTimeout(this.toastTimer)
		clearInterval(this.playbackCheckTimer)
		clearInterval(this.srcCheckTimer)
		// 优化：清理自动关闭定时器
		this.stopAutoCloseTimer()

		try {
			this.player?.dispose()
		} catch (e) {
			console.warn('Player dispose error:', e)
		}
		this.player = null
	},
	methods: {
		// ========== 清晰度同步 ==========

		getCurrentVideoSrc() {
			if (!this.player) return ''
			return document.querySelector(`#${this.id} video`)?.src
				|| this.player._playerOptions?.source
				|| this.player.source
				|| this.player.getOptions?.().source
				|| ''
		},

		startVideoSrcMonitor() {
			this.stopVideoSrcMonitor()
			let lastSrc = this.getCurrentVideoSrc()

			this.srcCheckTimer = setInterval(() => {
				if (!this.player) return
				const currentSrc = this.getCurrentVideoSrc()
				if (currentSrc && currentSrc !== lastSrc) {
					lastSrc = currentSrc
					this.syncCurrentQualityIndex()
				}
			}, 1000)
		},

		stopVideoSrcMonitor() {
			clearInterval(this.srcCheckTimer)
			this.srcCheckTimer = null
		},

		syncCurrentQualityIndex() {
			if (!this.player) return

			const availableQualities = QUALITY_ORDER.filter(q => this.videoUrls[q])
			const currentUrl = this.getCurrentVideoSrc()

			if (!currentUrl) return

			// 辅助函数：更新清晰度索引
			const updateQualityIndex = (quality) => {
				const newIndex = availableQualities.indexOf(quality)
				if (newIndex !== -1 && newIndex !== this.currentQualityIndex) {
					this.currentQualityIndex = newIndex
				}
			}

			// 方式1: 通过文件名后缀判断
			const fileName = this.getVideoUrlPath(currentUrl).split('/').pop()
			for (const [suffix, quality] of Object.entries(FILENAME_TO_QUALITY)) {
				if (fileName.endsWith(suffix) && availableQualities.includes(quality)) {
					updateQualityIndex(quality)
					return
				}
			}

			// 方式2: URL完整匹配（优先完全匹配，避免误判）
			for (const [quality, url] of Object.entries(this.videoUrls)) {
				if (currentUrl === url || url.endsWith(currentUrl) || currentUrl.endsWith(url)) {
					updateQualityIndex(quality)
					return
				}
			}

			// 方式3: 清晰度组件
			try {
				const qualityComponent = this.player.getComponent('QualityComponent')
				const currentQuality = qualityComponent?.currentQuality
				if (currentQuality) {
					const mappedQuality = DEFINITION_TO_QUALITY[currentQuality.definition] || currentQuality.definition
					if (availableQualities.includes(mappedQuality)) {
						updateQualityIndex(mappedQuality)
						return
					}

					const descQuality = DESC_TO_QUALITY[currentQuality.desc]
					if (descQuality && availableQualities.includes(descQuality)) {
						updateQualityIndex(descQuality)
					}
				}
			} catch (e) {
				console.warn('清晰度组件匹配失败:', e)
			}
		},

		// ========== UI交互 ==========

		showToastMsg(message, duration = 2000) {
			this.toastMessage = message
			this.showToast = true
			clearTimeout(this.toastTimer)
			this.toastTimer = setTimeout(() => {
				this.showToast = false
			}, duration)
		},

		handleTipConfirm() {
			this.showQualityTip = false
			this.stopAutoCloseTimer()
			this.syncCurrentQualityIndex()
			if (this.switchToLowerQuality()) {
				this.showToastMsg('正在切换清晰度...')
			}
		},

		handleTipCancel() {
			this.showQualityTip = false
			this.stopAutoCloseTimer()
			this.lastTipShowTime = 0 // 重置，允许下次立即弹窗
			this.showToastMsg('您可以在播放器控制栏手动切换清晰度', 3000)
		},

		handleTipClose() {
			this.showQualityTip = false
			this.stopAutoCloseTimer()
			this.lastTipShowTime = 0 // 重置，允许下次立即弹窗
		},

		// 优化：启动自动关闭定时器
		startAutoCloseTimer() {
			this.stopAutoCloseTimer()
			const showTipTime = Date.now() // 记录本次启动定时器的时间
			this.autoCloseTimer = setTimeout(() => {
				// 验证定时器是否对应当前弹窗（防止旧定时器关闭新弹窗）
				if (this.showQualityTip && Date.now() - showTipTime >= this.autoCloseDelay - 100) {
					this.showQualityTip = false
					console.log('[清晰度提示] 弹窗超时自动关闭')
				}
			}, this.autoCloseDelay)
		},

		// 优化：停止自动关闭定时器
		stopAutoCloseTimer() {
			if (this.autoCloseTimer) {
				clearTimeout(this.autoCloseTimer)
				this.autoCloseTimer = null
			}
		},

		// 优化：视频恢复播放时关闭弹窗
		closeTipOnPlaybackResume() {
			if (this.showQualityTip) {
				this.showQualityTip = false
				this.stopAutoCloseTimer()
				console.log('[清晰度提示] 视频恢复播放，自动关闭弹窗')
			}
		},

		showQualitySwitchTip() {
			// 优化：检查最小弹窗间隔
			const now = Date.now()
			if (this.lastTipShowTime > 0 && now - this.lastTipShowTime < this.minTipInterval) {
				console.log('[清晰度提示] 距离上次弹窗时间不足10秒，跳过本次提示')
				return
			}

			this.syncCurrentQualityIndex()
			const availableQualities = QUALITY_ORDER.filter(q => this.videoUrls[q])

			if (this.currentQualityIndex < 0 || this.currentQualityIndex >= availableQualities.length) {
				this.qualityTipText = '视频无法正常播放，请刷新页面重试'
				this.showQualityTip = true
				this.startAutoCloseTimer()
				this.lastTipShowTime = now
				return
			}

			const nextIndex = this.currentQualityIndex + 1
			if (nextIndex < availableQualities.length) {
				this.qualityTipText = `视频无法正常播放，建议切换到${availableQualities[nextIndex]}清晰度`
			} else if (this.getFallbackMediaType()) {
				this.qualityTipText = '视频无法正常播放，可尝试备用播放线路'
			} else {
				this.qualityTipText = '视频无法正常播放，请检查网络连接后重试'
			}
			this.showQualityTip = true
			this.startAutoCloseTimer()
			this.lastTipShowTime = now
		},

		// ========== 清晰度切换 ==========

		switchToLowerQuality() {
			const availableQualities = QUALITY_ORDER.filter(q => this.videoUrls[q])

			if (this.currentQualityIndex < 0 || this.currentQualityIndex >= availableQualities.length) {
				this.qualityTipText = '清晰度状态异常，请刷新页面重试'
				this.showQualityTip = true
				this.startAutoCloseTimer()
				return false
			}

			const nextIndex = this.currentQualityIndex + 1
			if (nextIndex >= availableQualities.length) {
				this.qualityTipText = '当前已是最低清晰度，请检查网络连接后重试'
				this.showQualityTip = true
				this.startAutoCloseTimer()
				return false
			}

			const nextQuality = availableQualities[nextIndex]
			const wasPlaying = this.player && !this.player.paused()
			const currentTime = this.player?.getCurrentTime() || 0

			this.currentQualityIndex = nextIndex

			if (this.player?.qualityUtils) {
				this.player.qualityUtils.changeQuality(nextQuality)
			} else if (this.player) {
				this.player.loadByUrl(this.videoUrls[nextQuality], currentTime)

				setTimeout(() => {
					const qualityComponent = this.player.getComponent('QualityComponent')
					qualityComponent?.setCurrentQuality(nextQuality, nextQuality)
				}, 200)

				if (wasPlaying) {
					setTimeout(() => {
						if (this.player?.paused()) {
							this.player.play()
						}
					}, 500)
				}
			}

			setTimeout(() => {
				this.showToastMsg(`已切换至${nextQuality}清晰度`)
			}, 300)

			return true
		},

		switchToFallbackMediaType() {
			const fallbackMediaType = this.getFallbackMediaType()
			if (!fallbackMediaType) return false

			const wasPlaying = this.player && !this.player.paused()
			const currentTime = this.player?.getCurrentTime() || 0
			const source = this.onSetVideoUrl(this.aliPlayOptions.source || [], fallbackMediaType)

			if (!Object.keys(this.videoUrls).length) return false

			this.currentQualityIndex = 0
			this.retryCount = 0

			if (this.player) {
				this.player.loadByUrl(source, currentTime)

				if (wasPlaying) {
					setTimeout(() => {
						if (this.player?.paused()) {
							this.player.play()
						}
					}, 500)
				}
			}

			setTimeout(() => {
				this.showToastMsg('已切换至备用播放线路')
			}, 300)

			return true
		},

		async smartRetry() {
			const availableQualities = QUALITY_ORDER.filter(q => this.videoUrls[q])
			const nextIndex = this.currentQualityIndex + 1

			if (nextIndex < availableQualities.length && this.retryCount < this.maxRetries) {
				this.retryCount++
				this.showToastMsg(`播放失败，尝试切换清晰度 (${this.retryCount}/${this.maxRetries})`, 2000)
				await new Promise(resolve => setTimeout(resolve, 1000))
				return this.switchToLowerQuality()
			}

			if (this.getFallbackMediaType()) {
				this.showToastMsg('播放失败，尝试备用播放线路', 2000)
				await new Promise(resolve => setTimeout(resolve, 1000))
				return this.switchToFallbackMediaType()
			}

			if (nextIndex >= availableQualities.length) {
				this.qualityTipText = '当前已是最低清晰度，播放仍然失败。请检查网络连接后刷新页面重试'
				this.showQualityTip = true
				this.startAutoCloseTimer()
				return false
			}

			this.retryCount = 0
			this.showQualitySwitchTip()
			return false
		},

		// ========== 播放状态检测 ==========

		checkPlaybackStatus() {
			if (!this.player || this.player.paused()) {
				this.isVideoStalled = false
				this.stallStartTime = 0
				this.stallCount = 0
				this.isBuffering = false
				this.bufferingStartTime = 0
				return
			}

			const now = Date.now()
			const currentTime = this.player.getCurrentTime()

			if (this.lastUpdateTime === 0) {
				this.lastUpdateTime = now
				this.lastCurrentTime = currentTime
				return
			}

			const timeAdvanced = currentTime - this.lastCurrentTime
			const timeElapsed = now - this.lastUpdateTime

			// 优化1：降低正常播放的判定阈值，从0.5秒改为0.1秒
			// 这样可以更好地识别网络慢但仍在播放的情况
			if (timeAdvanced >= 0.1) {
				// 播放正常，重置所有状态
				this.isVideoStalled = false
				this.stallStartTime = 0
				this.stallCount = 0
				this.isBuffering = false
				this.bufferingStartTime = 0
				this.lastCurrentTime = currentTime
				this.lastUpdateTime = now
				// 优化：视频恢复播放时自动关闭弹窗
				this.closeTipOnPlaybackResume()
			} else if (timeElapsed >= 2000) {
				// 2秒内进度前进不足0.1秒，可能卡顿或缓冲
				if (!this.isVideoStalled) {
					this.isVideoStalled = true
					this.stallStartTime = now
					this.lastUpdateTime = now
				} else {
					const stallDuration = now - this.stallStartTime

					// 优化2：区分缓冲和真实卡顿
					// 如果处于缓冲状态，给更长的等待时间（8秒）
					if (this.isBuffering) {
						const bufferDuration = now - this.bufferingStartTime
						if (bufferDuration >= 8000) {
							// 缓冲超过8秒，使用容错机制
							this.handleStallWithRetry()
						}
					} else if (stallDuration >= 5000) {
						// 优化3：非缓冲状态卡顿5秒，使用容错机制
						this.handleStallWithRetry()
					}
				}
			}
		},

		// 优化4：新增容错机制处理方法
		handleStallWithRetry() {
			// 防抖：如果弹窗已经在显示，不重复处理
			if (this.showQualityTip) {
				return
			}

			this.stallCount++
			this.showQualitySwitchTip()
			// 重置状态
			this.isVideoStalled = false
			this.stallStartTime = 0
			this.stallCount = 0
			this.isBuffering = false
			this.bufferingStartTime = 0
			this.lastUpdateTime = Date.now()
		},

		startPlaybackCheck() {
			if (this.playbackCheckTimer) return
			this.playbackCheckTimer = setInterval(() => this.checkPlaybackStatus(), 1000)
		},

		stopPlaybackCheck() {
			clearInterval(this.playbackCheckTimer)
			this.playbackCheckTimer = null
			this.isVideoStalled = false
			this.stallStartTime = 0
			this.stallCount = 0
			this.isBuffering = false
			this.bufferingStartTime = 0
			this.lastUpdateTime = 0
			this.lastCurrentTime = 0
		},

		// 优化5：新增缓冲状态标记方法
		markBufferingState(isBuffering) {
			this.isBuffering = isBuffering
			if (isBuffering) {
				this.bufferingStartTime = Date.now()
			} else {
				this.bufferingStartTime = 0
			}
		},

		// ========== 视频URL处理 ==========

		getVideoUrlPath(url = '') {
			return String(url || '').trim().split('?')[0].split('#')[0]
		},

		getVideoMediaType(url = '') {
			const urlPath = this.getVideoUrlPath(url)
			if (urlPath.endsWith('.mp4')) return 'mp4'
			if (urlPath.endsWith('.m3u8')) return 'm3u8'
			return ''
		},

		sortVideoUrls(videoUrls = {}) {
			const sorted = Object.entries(videoUrls)
				.sort((a, b) => QUALITY_ORDER.indexOf(a[0]) - QUALITY_ORDER.indexOf(b[0]))
			return Object.fromEntries(sorted)
		},

		getFallbackMediaType() {
			if (this.currentMediaType !== 'mp4') return ''
			return Object.keys(this.fallbackVideoUrls).length > 0 ? 'm3u8' : ''
		},

		onSetVideoUrl(url = [], targetMediaType = 'mp4') {
			if (!Array.isArray(url)) return JSON.stringify({})

			const sourceGroups = {
				mp4: {},
				m3u8: {}
			}

			url.forEach(item => {
				const sourceUrl = String(item || '').trim()
				const sourceMediaType = this.getVideoMediaType(sourceUrl)
				if (!sourceUrl || !sourceMediaType) return

				const fileName = this.getVideoUrlPath(sourceUrl).split('/').pop()

				for (const [suffix, quality] of Object.entries(FILENAME_TO_QUALITY)) {
					if (fileName.endsWith(suffix)) {
						sourceGroups[sourceMediaType][quality] = sourceUrl
						return
					}
				}
			})

			const sortedMp4 = this.sortVideoUrls(sourceGroups.mp4)
			const sortedM3u8 = this.sortVideoUrls(sourceGroups.m3u8)
			const fallbackMediaType = targetMediaType === 'mp4' ? 'm3u8' : 'mp4'
			const preferredSource = targetMediaType === 'mp4' ? sortedMp4 : sortedM3u8
			const fallbackSource = fallbackMediaType === 'mp4' ? sortedMp4 : sortedM3u8
			let preferredMediaType = targetMediaType

			if (!Object.keys(preferredSource).length && Object.keys(fallbackSource).length) {
				preferredMediaType = fallbackMediaType
			}

			this.currentMediaType = preferredMediaType
			this.videoUrls = preferredMediaType === 'mp4' ? sortedMp4 : sortedM3u8
			this.fallbackVideoUrls = preferredMediaType === 'mp4' ? sortedM3u8 : {}

			// 构建清晰度定义
			this.qualityDefinitions = Object.keys(this.videoUrls).map(quality => ({
				definition: quality,
				desc: QUALITY_NAMES[quality] || quality,
				URL: this.videoUrls[quality]
			}))

			// 按清晰度排序并返回
			return JSON.stringify(this.videoUrls)
		},

		// ========== 播放器初始化 ==========

		addEvent(options) {
			this.$emit('cancelFullScreen', options)
		},

		onPlayer() {
			const that = this

			// 清理旧的播放器和定时器，防止竞态条件
			this.stopVideoSrcMonitor()
			this.stopPlaybackCheck()
			this.stopAutoCloseTimer()
			this.player?.dispose()
			this.player = null

			this.currentQualityIndex = 0
			this.retryCount = 0

			const options = Object.assign({}, this.initOptions, this.aliPlayOptions, {
				id: this.id,
				source: this.onSetVideoUrl(this.aliPlayOptions.source || [])
			})

			this.player = new Aliplayer(options, (player) => {
				player.on('ready', () => {
					document.querySelector(`#${that.id} video`)
						?.addEventListener('webkitendfullscreen', that.addEvent.bind(that))
					that.startVideoSrcMonitor()
					setTimeout(() => that.syncCurrentQualityIndex(), 200)
				})

				player.on('sourceloaded', (params) => {
					const { paramData } = params
					player.getComponent('QualityComponent')?.setCurrentQuality(paramData.desc, paramData.definition)

					if (paramData.URL) {
						for (const [quality, url] of Object.entries(that.videoUrls)) {
							// 使用精确匹配避免误判
							if (paramData.URL === url || url.endsWith(paramData.URL) || paramData.URL.endsWith(url)) {
								const availableQualities = QUALITY_ORDER.filter(q => that.videoUrls[q])
								const newIndex = availableQualities.indexOf(quality)
								if (newIndex !== -1) {
									that.currentQualityIndex = newIndex
									break
								}
							}
						}
					}
				})

				player.on('qualityChange', (params) => {
					if (params?.paramData?.URL) {
						for (const [quality, url] of Object.entries(that.videoUrls)) {
							// 使用精确匹配避免误判
							if (params.paramData.URL === url || url.endsWith(params.paramData.URL) || params.paramData.URL.endsWith(url)) {
								const availableQualities = QUALITY_ORDER.filter(q => that.videoUrls[q])
								const newIndex = availableQualities.indexOf(quality)
								if (newIndex !== -1) {
									that.currentQualityIndex = newIndex
									break
								}
							}
						}
					}
				})

				player.on('play', (opts) => { that.$emit('play', opts); that.startPlaybackCheck() })
				player.on('pause', (opts) => { that.$emit('pause', opts); that.stopPlaybackCheck() })
				player.on('timeupdate', (opts) => that.$emit('timeupdate', opts))
				player.on('x5requestFullScreen', (opts) => that.$emit('fullScreen', opts))
				player.on('x5cancelFullScreen', (opts) => that.$emit('cancelFullScreen', opts))
				player.on('requestFullScreen', (opts) => that.$emit('fullScreen', opts))
				player.on('cancelFullScreen', (opts) => that.$emit('cancelFullScreen', opts))
				player.on('ended', (opts) => { that.$emit('ended', opts); that.stopPlaybackCheck() })

				// 优化6：监听播放器缓冲事件，区分缓冲和真实卡顿
				player.on('waiting', () => {
					that.markBufferingState(true)
				})
				player.on('playing', () => {
					that.markBufferingState(false)
				})
				player.on('canplay', () => {
					that.markBufferingState(false)
				})

				player.on('error', (opts) => {
					const errorData = opts.paramData || {}
					that.$analysysAgentTrack('doctor_ksh_video_error', {
						error_code: errorData.error_code,
						error_msg: errorData.error_msg,
						media_type: that.currentMediaType,
						video_source: that.getCurrentVideoSrc(),
						video_qualities: Object.keys(that.videoUrls).join(',')
					})
					that.$emit('error', opts)

					const { error_code } = errorData
					if (error_code === 4011) {
						// 重新初始化播放器
						that.player?.dispose()
						that.onPlayer()
					} else if ([4400, 4440, 4003, 4001].includes(error_code)) {
						that.smartRetry().then((switched) => {
							if (!switched) {
								document.querySelector('.prism-error-content>p').innerHTML =
									'由于服务器或网络原因不能加载资源，或者格式不支持'
							}
						})
					}
				})
			})

			this.player.isFullscreen = () => that.player.fullscreenService.getIsFullScreen()
			return this.player
		},

		measureBW(fn) {
			const ua = navigator.userAgent
			if (!ua) {
				this.speed = false
				fn(true, false)
				return
			}

			const match = ua.match(/NetType\/\w+/)
			const networkStr = match ? match[0].toLowerCase().replace('nettype/', '') : 'other'
			const isHighSpeed = ['wifi', '5g', '4g', 'other'].includes(networkStr)

			this.speed = isHighSpeed
			fn(true, isHighSpeed)
		}
	}
}
</script>

<style>
.prism-player .prism-controlbar { background: rgba(0, 0, 0, 0.5); }
.prism-player .prism-big-play-btn {
	left: calc(50% - 32px) !important;
	bottom: calc(50% - 32px) !important;
}
.prism-cc-btn { display: none; }
.playerBox video { object-fit: contain !important; height: 100% !important; }
.playerBox .current-quality { width: 50px; }
.playerBox .quality-components { margin: 7px 5px 0 0; }
</style>

<style lang="scss" scoped>
.playerBox {
	position: relative;
	left: 0;
	top: 0;
	z-index: 1100;
	background: #000;
	max-height: 100%;
}

.quality-tip-card {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: rgba(0, 0, 0, 0.85);
	backdrop-filter: blur(10px);
	border-radius: 8px;
	padding: 16px 20px;
	max-width: 320px;
	min-width: 260px;
	z-index: 9999;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
	animation: qualityTipFadeIn 0.3s ease-out;
}

.quality-tip-slide-enter-active, .quality-tip-slide-leave-active {
	transition: all 0.3s ease;
}

.quality-tip-slide-enter-from, .quality-tip-slide-leave-to {
	opacity: 0;
	transform: translate(-50%, -50%) scale(0.9);
}

@keyframes qualityTipFadeIn {
	from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
	to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

.quality-tip-header {
	display: flex;
	align-items: center;
	gap: 6px;
	margin-bottom: 8px;
}

.quality-tip-icon { font-size: 16px; line-height: 1; }

.quality-tip-title {
	flex: 1;
	font-size: 13px;
	font-weight: 600;
	color: #faad14;
}

.quality-tip-close {
	font-size: 14px;
	color: rgba(255, 255, 255, 0.6);
	cursor: pointer;
	padding: 2px;
	line-height: 1;
	transition: color 0.2s;
	&:hover { color: rgba(255, 255, 255, 0.9); }
}

.quality-tip-text {
	font-size: 12px;
	color: rgba(255, 255, 255, 0.9);
	line-height: 1.5;
	margin-bottom: 10px;
}

.quality-tip-actions {
	display: flex;
	gap: 8px;
	margin-top: 8px;
}

.tip-btn {
	flex: 1;
	padding: 6px 12px;
	border-radius: 4px;
	font-size: 12px;
	cursor: pointer;
	border: none;
	transition: all 0.2s;

	&.tip-btn-cancel {
		background: rgba(255, 255, 255, 0.15);
		color: rgba(255, 255, 255, 0.85);
		&:hover { background: rgba(255, 255, 255, 0.25); }
	}

	&.tip-btn-confirm {
		background: #1890ff;
		color: #fff;
		&:hover { background: #40a9ff; }
	}
}

.toast-toast {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: rgba(0, 0, 0, 0.8);
	color: #fff;
	padding: 12px 24px;
	border-radius: 8px;
	font-size: 14px;
	z-index: 10000;
	animation: toastFadeIn 0.3s ease-out;
	max-width: 80%;
	text-align: center;
}

@keyframes toastFadeIn {
	from { opacity: 0; transform: translate(-50%, -50%) scale(0.9); }
	to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}
</style>
